import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover.tsx";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { cn } from "@/lib/utils.ts";
import type React from "react";

/**
 * Minimal contract Radix's PopoverAnchor needs for positioning (mirrors
 * Floating UI's `VirtualElement` shape). Lets callers pass an in-memory
 * rect instead of an actual DOM node — useful when the "thing the popover
 * anchors to" is a computed union of grid cells with no single owner.
 */
type VirtualAnchor = { getBoundingClientRect: () => DOMRect };

interface AgentPopoverProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /**
   * Anchor for the popover on desktop. Pass an HTMLElement when there's
   * a real DOM node to anchor against, or a virtual element (anything
   * with `getBoundingClientRect`) when the anchor is a computed rect.
   * Ignored on mobile (bottom sheet, no anchoring).
   */
  anchor: HTMLElement | VirtualAnchor | null;
  /** Body content. Owns its own layout — input placement, chips, action rows. */
  children: React.ReactNode;
  /** Accessible label for the popover / sheet root. */
  ariaLabel: string;
  /** Preferred side on desktop. Radix's collision detection may flip it. */
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  /** Visual width on desktop. Defaults to the spec's 400px. */
  widthPx?: number;
  /**
   * Extra classes for the card body (the padded container around `children`).
   * Merged after the defaults, so e.g. `"p-0"` removes the default `p-3` for
   * callers that embed a component owning its own padding/scroll.
   */
  bodyClassName?: string;
  /**
   * Forwarded to Radix `PopoverContent`. Call `event.preventDefault()` to
   * keep the popover open — e.g. when the click lands on a calendar cell
   * the parent wants to handle directly without dismissing the popover.
   */
  onInteractOutside?: React.ComponentProps<typeof PopoverContent>["onInteractOutside"];
}

const DEFAULT_WIDTH_PX = 400;
const POPOVER_GAP_PX = 8;

/**
 * Shell for the three anchored agent triggers (event-click, range-select,
 * drag-with-guests). Desktop renders a Radix Popover anchored to whatever the
 * user just acted on; mobile renders a non-modal fixed-bottom panel. Both
 * variants wrap their content in a unified card (`rounded-2xl border
 * bg-popover shadow-2xl`) so suggestion chips and the input read as one
 * cohesive panel rather than floating siblings.
 *
 * Mobile is deliberately NOT a Radix Sheet — the sheet's modal overlay
 * intercepts pointer events, which broke critical calendar interactions
 * (dragging the range resize handles, tapping a selected cell to remove
 * it, etc.). The fixed-bottom div lets the calendar above stay fully
 * interactive while the popover sits pinned to the bottom edge. Dismissal
 * still works through the body's own affordances (chip X, popover X) and
 * the selection lifecycle (clearing ranges closes the range popover).
 *
 * `CreateOrAskDialog` does NOT compose this — it's a centered Dialog with no
 * anchor, so it has its own shape.
 */
export const AgentPopover = ({
  open,
  onOpenChange,
  anchor,
  children,
  ariaLabel,
  side,
  align = "start",
  widthPx = DEFAULT_WIDTH_PX,
  bodyClassName,
  onInteractOutside,
}: AgentPopoverProps) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    if (!open) return null;
    return (
      <div
        role="dialog"
        aria-label={ariaLabel}
        // Fixed-bottom non-modal panel. No overlay, no backdrop — the
        // calendar above stays interactive so the user can drag the
        // range resize handles, tap to add/remove ranges, scroll, etc.
        // `pointer-events-none` on the wrapper plus `pointer-events-auto`
        // on the inner panel keeps the dead space to either side of the
        // panel from intercepting clicks meant for the calendar.
        className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 pointer-events-none"
      >
        {/* Unified card — same chrome as the desktop popover so both
            variants present the body inside one cohesive panel. */}
        <div
          className={cn(
            "pointer-events-auto max-h-[60vh] overflow-y-auto",
            "rounded-2xl border bg-popover text-popover-foreground p-3 shadow-2xl",
            "data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-2",
            bodyClassName,
          )}
          data-state="open"
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      {anchor ? (
        // `virtualRef` accepts either an HTMLElement or any object with
        // `getBoundingClientRect`. We pass the union type through unchanged.
        <PopoverAnchor virtualRef={{ current: anchor as HTMLElement }} />
      ) : null}
      <PopoverContent
        side={side}
        align={align}
        sideOffset={POPOVER_GAP_PX}
        collisionPadding={8}
        aria-label={ariaLabel}
        onInteractOutside={onInteractOutside}
        style={{ width: widthPx }}
        className={cn(
          "rounded-2xl border bg-popover text-popover-foreground p-3 shadow-lg",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
          bodyClassName,
        )}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
};
